var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { endOfDay, format } from "date-fns";
import { willNeverHappen } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { getObservableT, i18nGetDateFnsLocale } from "../Infra/i18next";
import { PatientConsentViewModel } from "./PatientConsentViewModel";
const t = getObservableT("patients");
export const hipaaContentLoadedClassName = "hipaaContentLoaded";
export class PatientConsentSignatureViewModel extends PatientConsentViewModel {
    get showFooter() {
        return (!this.isPDDEGeneralDataUse || (this.isPDDEGeneralDataUse && this.currentStep === "signature" && !this.showDeclineConfirmation));
    }
    constructor(container) {
        super(container);
        this.signatureType = null;
        this.isValidSignature = null;
        this.isValidSigningReason = null;
        this.isHealthExAccessChecked = null;
        this.isValidHealthExAccess = true;
        this.isHaveReadPolicyChecked = null;
        this.isValidHaveReadPolicy = true;
        this.isValidHaveReadTEFCAPolicy = true;
        this.currentStep = "signature";
        this.showDeclineConfirmation = false;
        this.faqsDialogOpen = false;
        this.updateIsValidSignature();
    }
    run() {
        this.disposer.reaction(() => this.isConsentScreenshotMode, () => {
            if (this.isConsentScreenshotMode) {
                this.updateHealthExAccess(true);
                this.updateHaveReadPolicy(true);
            }
        }, { fireImmediately: true, name: "PatientConsentSignatureViewModel_isConsentScreenshotMode" });
    }
    stop() {
        this.disposer.dispose();
    }
    get title() {
        if (this.isPDDEGeneralDataUse)
            return t("Confirm access to your records");
        if (!this.signatureType)
            return null;
        switch (this.signatureType) {
            case "parentGuardian":
                return this.getCustomizedText("parentGuardianSignatureTitle");
            case "additionalParentGuardian":
                return this.getCustomizedText("additionalParentGuardianSignatureTitle");
            case "minor":
                return this.getCustomizedText("minorParticipantAssentTitle");
            case "participant":
                return this.getCustomizedText("participantTitle");
            case "witness":
                return this.getCustomizedText("witnessSignatureTitle");
            case "provider":
                return this.getCustomizedText("providerSignatureTitle");
            default:
                return willNeverHappen(this.signatureType);
        }
    }
    get isScreenshotMode() {
        return this.patientConsentUIManager.isScreenshotMode;
    }
    get isConsentScreenshotMode() {
        return this.patientConsentUIManager.isScreenshotMode && !this.patientConsentUIManager.isScreenshotPreviewMode;
    }
    get contentStatement() {
        return this.getCustomizedText("informedConsentStatement");
    }
    get aiToolName() {
        return this.getCustomizedText("aiToolName");
    }
    get aiAuthToShare() {
        return this.getCustomizedText("aiAuthToShare");
    }
    get aiAuthToShareExplanation() {
        return this.getCustomizedText("aiAuthToShareExplanation");
    }
    get aiToolTermsUrl() {
        var _a;
        return (_a = this.getCustomizedText("aiToolTermsUrl")) !== null && _a !== void 0 ? _a : "";
    }
    get consentFlowPageData() {
        if (!this.isScreenshotMode)
            return null;
        return this.patientConsentUIManager.consentFlowPageData;
    }
    get todaysDate() {
        var _a, _b;
        const date = ((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.signingTimestamp) !== undefined && ((_b = this.consentFlowPageData) === null || _b === void 0 ? void 0 : _b.signingTimestamp) !== null
            ? new Date(this.consentFlowPageData.signingTimestamp)
            : endOfDay(new Date());
        return format(date, "MM-dd-yyyy", { locale: i18nGetDateFnsLocale() });
    }
    get signature() {
        if (!this.isProvider && this.signatureType === "parentGuardian") {
            return this.parentGuardianName;
        }
        else if (!this.isProvider && this.signatureType === "additionalParentGuardian") {
            return this.additionalParentGuardianName;
        }
        else if (this.signatureType === "minor" || this.signatureType === "participant") {
            return this.patientName;
        }
        else if (this.signatureType === "provider") {
            return this.providerName;
        }
        return null;
    }
    get healthExAccessText() {
        return this.isIAS
            ? t("I authorize HealthEx to access my records")
            : t("I authorize HealthEx to access and share my records for this request");
    }
    get declineConsentText() {
        if (this.isAIFlow) {
            return t("By declining, you won’t connect your records with {{ aiToolName }}. You can review and change access settings anytime.", {
                aiToolName: this.aiToolName,
            });
        }
        return this.isIAS
            ? t("By declining, you won’t get your HealthEx Unified Record. You can do this later from the HealthEx portal.")
            : t("By declining, you won't share your records with {{ orgName }}. You can review and change access settings anytime.", {
                orgName: this.organizationName,
            });
    }
    updateSignature(signature) {
        if (this.signatureType === null)
            return;
        if (this.signatureType === "parentGuardian") {
            this.patientConsentUIManager.updateParentGuardianSignature(signature);
        }
        else if (this.signatureType === "additionalParentGuardian") {
            this.patientConsentUIManager.updateAdditionalParentGuardianSignature(signature);
        }
        else if (this.signatureType === "minor") {
            this.patientConsentUIManager.updateMinorParticipantSignature(signature);
            this.patientConsentUIManager.updateUserSignatureHIPAA(signature);
        }
        else if (this.signatureType === "participant") {
            this.patientConsentUIManager.updateUserSignatureHIPAA(signature);
        }
        else if (this.signatureType === "witness") {
            this.patientConsentUIManager.updateWitnessSignature(signature);
        }
        else if (this.signatureType === "provider") {
            this.patientConsentUIManager.updateProviderSignature(signature);
        }
        else {
            willNeverHappen(this.signatureType);
        }
        if (this.isValidSignature === false || this.isValidSignature === null) {
            this.updateIsValidSignature();
        }
    }
    updateIsValidSignature() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if ((this.signatureType === "parentGuardian" && !!((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.parentGuardianSignerName)) ||
            (this.signatureType === "additionalParentGuardian" && !!((_b = this.consentFlowPageData) === null || _b === void 0 ? void 0 : _b.additionalParentGuardianSignerName)) ||
            (this.signatureType === "minor" && !!((_c = this.consentFlowPageData) === null || _c === void 0 ? void 0 : _c.minorParticipantSignerName)) ||
            (this.signatureType === "participant" && !!((_d = this.consentFlowPageData) === null || _d === void 0 ? void 0 : _d.signerName)) ||
            (this.signatureType === "witness" && !!((_e = this.consentFlowPageData) === null || _e === void 0 ? void 0 : _e.witnessSignerName)) ||
            (this.signatureType === "provider" && !!((_f = this.consentFlowPageData) === null || _f === void 0 ? void 0 : _f.providerSignerName))) {
            this.isValidSignature = true;
        }
        else if (this.inputSignature === null) {
            this.isValidSignature = null;
        }
        else if (this.signature === null) {
            this.isValidSignature = ((_g = this.inputSignature) !== null && _g !== void 0 ? _g : "").trim().length > 0;
        }
        else {
            this.isValidSignature = ((_h = this.inputSignature) !== null && _h !== void 0 ? _h : "").trimEnd() === ((_j = this.signature) !== null && _j !== void 0 ? _j : "").trimEnd();
        }
    }
    get inputSignature() {
        var _a, _b, _c, _d, _e, _f;
        if (this.signatureType === null)
            return null;
        if (this.signatureType === "parentGuardian") {
            return ((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.parentGuardianSignerName)
                ? this.consentFlowPageData.parentGuardianSignerName
                : this.patientConsentUIManager.session.parentGuardianSignature;
        }
        else if (this.signatureType === "additionalParentGuardian") {
            return ((_b = this.consentFlowPageData) === null || _b === void 0 ? void 0 : _b.additionalParentGuardianSignerName)
                ? this.consentFlowPageData.additionalParentGuardianSignerName
                : this.patientConsentUIManager.session.additionalParentGuardianSignature;
        }
        else if (this.signatureType === "minor") {
            return ((_c = this.consentFlowPageData) === null || _c === void 0 ? void 0 : _c.minorParticipantSignerName)
                ? this.consentFlowPageData.minorParticipantSignerName
                : this.patientConsentUIManager.session.minorParticipantSignature;
        }
        else if (this.signatureType === "participant") {
            return ((_d = this.consentFlowPageData) === null || _d === void 0 ? void 0 : _d.signerName)
                ? this.consentFlowPageData.signerName
                : this.patientConsentUIManager.session.patientSignatureHIPAA;
        }
        else if (this.signatureType === "witness") {
            return ((_e = this.consentFlowPageData) === null || _e === void 0 ? void 0 : _e.witnessSignerName)
                ? this.consentFlowPageData.witnessSignerName
                : this.patientConsentUIManager.session.witnessSignature;
        }
        else if (this.signatureType === "provider") {
            return ((_f = this.consentFlowPageData) === null || _f === void 0 ? void 0 : _f.providerSignerName)
                ? this.consentFlowPageData.providerSignerName
                : this.patientConsentUIManager.session.providerSignature;
        }
        return willNeverHappen(this.signatureType);
    }
    get researchUIText() {
        if (this.signatureType === null)
            return null;
        return this.signatureType === "parentGuardian"
            ? this.getCustomizedText("parentGuardianInformedConsentStatement")
            : this.signatureType === "additionalParentGuardian"
                ? this.getCustomizedText("additionalParentGuardianInformedConsentStatement")
                : this.signatureType === "minor"
                    ? this.getCustomizedText("minorParticipantAssentStatement")
                    : this.signatureType === "participant"
                        ? this.getCustomizedText("informedConsentStatement")
                        : this.signatureType === "witness"
                            ? this.getCustomizedText("witnessConfirmation")
                            : this.signatureType === "provider"
                                ? this.getCustomizedText("providerConfirmation")
                                : willNeverHappen(this.signatureType);
    }
    get isSignatureTextFieldEnabled() {
        return !this.patientConsentUIManager.session.isSubmittingConsent;
    }
    get signingReason() {
        var _a, _b, _c, _d, _e, _f;
        if (this.isPDDEGeneralDataUse)
            return null;
        if (!this.require21CFRPart11ForInformedConsents)
            return null;
        if (this.signatureType === "parentGuardian") {
            return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.parentGuardianSigningReason) !== null && _b !== void 0 ? _b : t("I have read and am providing consent for my child or ward");
        }
        else if (this.signatureType === "additionalParentGuardian") {
            return ((_d = (_c = this.consentFlowPageData) === null || _c === void 0 ? void 0 : _c.additionalParentGuardianSigningReason) !== null && _d !== void 0 ? _d : t("I have read and am providing consent for my child or ward"));
        }
        else if (this.signatureType === "participant") {
            return (_f = (_e = this.consentFlowPageData) === null || _e === void 0 ? void 0 : _e.signingReason) !== null && _f !== void 0 ? _f : t("I have read and am providing informed consent");
        }
        return null;
    }
    get inputSigningReason() {
        var _a, _b, _c, _d, _e, _f;
        if (this.signatureType === "parentGuardian") {
            return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.parentGuardianSigningReason) !== null && _b !== void 0 ? _b : this.patientConsentUIManager.session.parentGuardianSigningReason;
        }
        else if (this.signatureType === "additionalParentGuardian") {
            return ((_d = (_c = this.consentFlowPageData) === null || _c === void 0 ? void 0 : _c.additionalParentGuardianSigningReason) !== null && _d !== void 0 ? _d : this.patientConsentUIManager.session.additionalParentGuardianSigningReason);
        }
        else if (this.signatureType === "participant") {
            return (_f = (_e = this.consentFlowPageData) === null || _e === void 0 ? void 0 : _e.signingReason) !== null && _f !== void 0 ? _f : this.patientConsentUIManager.session.patientSigningReasonHIPAA;
        }
        return null;
    }
    get footerProperties() {
        return {
            confirmButtonText: this.isPDDEGeneralDataUse ? t("Consent & confirm access") : t("Confirm and continue"),
            onConfirmButtonClicked: () => this.confirmAndContinue(),
            secondaryButtonText: this.isPDDEGeneralDataUse ? t("Decline") : undefined,
            onSecondaryButtonClicked: () => (this.isPDDEGeneralDataUse ? (this.currentStep = "declineConfirmation") : undefined),
            isSecondaryButtonDestructive: this.isPDDEGeneralDataUse ? false : undefined,
            isSecondaryButtonTypeText: this.isPDDEGeneralDataUse,
            hidePrivacyButton: this.isPDDEGeneralDataUse,
        };
    }
    updateSigningReason(checked, signingReason) {
        const reason = checked ? signingReason : null;
        if (this.signatureType === "parentGuardian") {
            this.patientConsentUIManager.updateParentGuardianSigningReason(reason);
        }
        else if (this.signatureType === "additionalParentGuardian") {
            this.patientConsentUIManager.updateAdditionalParentGuardianSigningReason(reason);
        }
        else if (this.signatureType === "participant") {
            this.patientConsentUIManager.updateUserSigningReasonHIPAA(reason);
        }
        if (this.isValidSigningReason === false) {
            this.updateIsValidSigningReason();
        }
    }
    updateIsValidSigningReason() {
        this.isValidSigningReason = this.signingReason === null || !!this.inputSigningReason;
    }
    updateHealthExAccess(checked) {
        this.isHealthExAccessChecked = checked;
        if (checked) {
            this.isValidHealthExAccess = checked;
        }
    }
    toggleHealthExAccess() {
        this.updateHealthExAccess(!this.isHealthExAccessChecked);
    }
    updateHaveReadPolicy(checked) {
        this.isHaveReadPolicyChecked = checked;
        if (checked) {
            this.isValidHaveReadPolicy = checked;
        }
    }
    toggleHaveReadPolicy() {
        this.updateHaveReadPolicy(!this.isHaveReadPolicyChecked);
    }
    updateIsValidPDDEChecks() {
        this.isValidHealthExAccess = this.isHealthExAccessChecked === true;
        this.isValidHaveReadPolicy = this.isHaveReadPolicyChecked === true;
    }
    get signingReasonErrorMessage() {
        if (this.isPDDEGeneralDataUse && (!this.isValidHealthExAccess || !this.isValidHaveReadPolicy || !this.isValidHaveReadTEFCAPolicy)) {
            return t("Please check both boxes to continue");
        }
        if (this.isValidSigningReason === false) {
            return t("Please acknowledge the meaning of signing before continuing.");
        }
        return null;
    }
    async confirmAndContinue() {
        this.updateIsValidSignature();
        this.updateIsValidSigningReason();
        this.updateIsValidPDDEChecks();
        if (!this.isValidSignature) {
            this.isValidSignature = false;
            return;
        }
        if (this.isPDDEGeneralDataUse && (!this.isValidHealthExAccess || !this.isValidHaveReadPolicy || !this.isValidHaveReadTEFCAPolicy)) {
            return;
        }
        else if (!this.isValidSigningReason) {
            return;
        }
        if (this.isIAS || this.isPDDEGeneralDataUse) {
            this.patientConsentUIManager.updatePatientInformedConsentEnabled(true, null);
        }
        await this.confirmConsentOrGoToNextPage();
    }
    onGoBackClicked() {
        this.currentStep = "signature";
    }
    showFaqsDialog() {
        this.faqsDialogOpen = true;
    }
    hideFaqsDialog() {
        this.faqsDialogOpen = false;
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "signatureType", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "isValidSignature", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "isValidSigningReason", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "isHealthExAccessChecked", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "isValidHealthExAccess", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "isHaveReadPolicyChecked", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "isValidHaveReadPolicy", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "isValidHaveReadTEFCAPolicy", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], PatientConsentSignatureViewModel.prototype, "currentStep", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], PatientConsentSignatureViewModel.prototype, "showDeclineConfirmation", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "showFooter", null);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentSignatureViewModel.prototype, "faqsDialogOpen", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "title", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "isScreenshotMode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "isConsentScreenshotMode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "contentStatement", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "aiToolName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "aiAuthToShare", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "aiAuthToShareExplanation", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "aiToolTermsUrl", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "consentFlowPageData", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "todaysDate", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "signature", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "healthExAccessText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "declineConsentText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "inputSignature", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "researchUIText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "isSignatureTextFieldEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "signingReason", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "inputSigningReason", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "footerProperties", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentSignatureViewModel.prototype, "signingReasonErrorMessage", null);
